import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config';
import AdminLayout from './AdminLayout';
import './Admin.css';

function AdminAnalytics() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState('7d');
    const { currentUser } = useAuth();

    useEffect(() => {
        fetchAnalytics();
    }, [dateRange]);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!currentUser) {
                throw new Error('Not authenticated');
            }

            const token = await currentUser.getIdToken();

            const response = await fetch(`${API_URL}/api/admin/analytics/overview?range=${dateRange}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server error: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                setAnalytics(data.analytics);
            } else {
                // Use placeholder data if API returns empty
                setAnalytics(getPlaceholderAnalytics());
            }
        } catch (err) {
            console.error('Analytics fetch error:', err);
            setError(err.message);
            // Set placeholder data so page doesn't crash
            setAnalytics(getPlaceholderAnalytics());
        } finally {
            setLoading(false);
        }
    };

    const getPlaceholderAnalytics = () => ({
        users: { total: 0, newToday: 0, activeToday: 0, retentionRate: 0 },
        subscriptions: { premium: 0, free: 0, conversionRate: 0, churnRate: 0 },
        revenue: { mrr: 0, today: 0, thisMonth: 0, arpu: 0, mrrGrowth: 0 },
        content: { topicsGenerated: 0, lessonsCompleted: 0, quizzesTaken: 0, avgSessionTime: '0m' },
        topTopics: []
    });

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const formatPercent = (value) => {
        return `${(value || 0).toFixed(1)}%`;
    };

    // Simple bar chart component
    const SimpleBarChart = ({ data, label }) => {
        const maxValue = Math.max(...(data || []).map(d => d.value), 1);
        return (
            <div className="simple-chart">
                <div className="chart-bars">
                    {(data || []).map((item, idx) => (
                        <div key={idx} className="chart-bar-container">
                            <div
                                className="chart-bar"
                                style={{ height: `${(item.value / maxValue) * 100}%` }}
                            >
                                <span className="bar-value">{item.value}</span>
                            </div>
                            <span className="bar-label">{item.label}</span>
                        </div>
                    ))}
                </div>
                <span className="chart-title">{label}</span>
            </div>
        );
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="admin-loading-container">
                    <div className="spinner"></div>
                    <p>Loading analytics...</p>
                </div>
            </AdminLayout>
        );
    }

    if (error) {
        return (
            <AdminLayout>
                <div className="admin-error">
                    <h2>Error Loading Analytics</h2>
                    <p>{error}</p>
                    <button onClick={fetchAnalytics}>Retry</button>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-analytics">
                <div className="admin-header">
                    <h1>Analytics</h1>
                    <div className="date-range-selector">
                        {['24h', '7d', '30d', '90d'].map(range => (
                            <button
                                key={range}
                                className={`range-btn ${dateRange === range ? 'active' : ''}`}
                                onClick={() => setDateRange(range)}
                            >
                                {range}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Revenue Section */}
                <section className="analytics-section">
                    <h2>Revenue</h2>
                    <div className="metrics-grid">
                        <div className="metric-card large">
                            <span className="metric-label">Monthly Recurring Revenue</span>
                            <span className="metric-value">{formatCurrency(analytics?.revenue?.mrr)}</span>
                            <span className="metric-change positive">
                                +{formatPercent(analytics?.revenue?.mrrGrowth)} from last month
                            </span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Revenue Today</span>
                            <span className="metric-value">{formatCurrency(analytics?.revenue?.today)}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Revenue This Month</span>
                            <span className="metric-value">{formatCurrency(analytics?.revenue?.thisMonth)}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Avg Revenue Per User</span>
                            <span className="metric-value">{formatCurrency(analytics?.revenue?.arpu)}</span>
                        </div>
                    </div>
                </section>

                {/* Users Section */}
                <section className="analytics-section">
                    <h2>Users</h2>
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <span className="metric-label">Total Users</span>
                            <span className="metric-value">{analytics?.users?.total || 0}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">New Users Today</span>
                            <span className="metric-value">{analytics?.users?.newToday || 0}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Active Today</span>
                            <span className="metric-value">{analytics?.users?.activeToday || 0}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Retention Rate</span>
                            <span className="metric-value">{formatPercent(analytics?.users?.retentionRate)}</span>
                        </div>
                    </div>

                    <div className="chart-container">
                        <SimpleBarChart
                            data={[
                                { label: 'Mon', value: 45 },
                                { label: 'Tue', value: 52 },
                                { label: 'Wed', value: 49 },
                                { label: 'Thu', value: 63 },
                                { label: 'Fri', value: 58 },
                                { label: 'Sat', value: 41 },
                                { label: 'Sun', value: 38 }
                            ]}
                            label="Daily Active Users (Last 7 Days)"
                        />
                    </div>
                </section>

                {/* Subscriptions Section */}
                <section className="analytics-section">
                    <h2>Subscriptions</h2>
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <span className="metric-label">Premium Users</span>
                            <span className="metric-value premium">{analytics?.subscriptions?.premium || 0}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Free Users</span>
                            <span className="metric-value">{analytics?.subscriptions?.free || 0}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Conversion Rate</span>
                            <span className="metric-value">{formatPercent(analytics?.subscriptions?.conversionRate)}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Churn Rate</span>
                            <span className="metric-value warning">{formatPercent(analytics?.subscriptions?.churnRate)}</span>
                        </div>
                    </div>

                    <div className="subscription-breakdown">
                        <h3>Subscription Breakdown</h3>
                        <div className="breakdown-bar">
                            <div
                                className="breakdown-segment premium"
                                style={{ width: `${((analytics?.subscriptions?.premium || 0) / ((analytics?.subscriptions?.premium || 0) + (analytics?.subscriptions?.free || 1))) * 100}%` }}
                            >
                                Premium
                            </div>
                            <div
                                className="breakdown-segment free"
                                style={{ width: `${((analytics?.subscriptions?.free || 0) / ((analytics?.subscriptions?.premium || 0) + (analytics?.subscriptions?.free || 1))) * 100}%` }}
                            >
                                Free
                            </div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="analytics-section">
                    <h2>Content & Engagement</h2>
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <span className="metric-label">Topics Generated</span>
                            <span className="metric-value">{analytics?.content?.topicsGenerated || 0}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Lessons Completed</span>
                            <span className="metric-value">{analytics?.content?.lessonsCompleted || 0}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Quizzes Taken</span>
                            <span className="metric-value">{analytics?.content?.quizzesTaken || 0}</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Avg Session Time</span>
                            <span className="metric-value">{analytics?.content?.avgSessionTime || '0m'}</span>
                        </div>
                    </div>
                </section>

                {/* Top Topics */}
                <section className="analytics-section">
                    <h2>Popular Topics</h2>
                    <div className="top-topics-list">
                        {(analytics?.topTopics || [
                            { name: 'Python Programming', count: 145 },
                            { name: 'JavaScript', count: 132 },
                            { name: 'Machine Learning', count: 98 },
                            { name: 'Spanish Language', count: 87 },
                            { name: 'Guitar', count: 76 }
                        ]).map((topic, idx) => (
                            <div key={idx} className="topic-rank-item">
                                <span className="rank">#{idx + 1}</span>
                                <span className="topic-name">{topic.name}</span>
                                <span className="topic-count">{topic.count} users</span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </AdminLayout>
    );
}

export default AdminAnalytics;
